import React, { FC, useState } from 'react';
import qs from 'qs';
import { omit } from 'lodash';
import { useHistory, useLocation } from 'react-router-dom';
import { trackEvent } from '@src/metrics';
import { closeModal } from '@src/redux/actions/modal';
import { Stack } from '@src/components/Stack';
import { TalentCard } from '@src/containers/Source/TalentCard';
import { getRecruiter } from '@src/redux/selectors/recruiter';
import { DEFAULT_CURRENCY_CODE, VIEW_VERIFIED_USER_QUERY_PARAM } from '@src/constants';
import {
  useCreateVerifiedUserApplicantMutation,
  useGetVerifiedUserQuery,
  useSessionQuery,
} from '@src/graphql/generated';
import { CircularLoading, Size } from '@src/components/CircularLoading';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { getInviteMessage } from '@src/util/getInviteMessage';
import { useAppDispatch, useAppSelector } from '@src/redux';
import { SendMessage } from './SendMessage';
import { MessageSent } from './MessageSent';
import { InviteToInterviewViews } from './enums/inviteToInterviewViews.enum';
import { InviteToInterviewProps } from './props';
import './styles.scss';

export const InviteToInterview: FC<InviteToInterviewProps> = ({
  onMessaged,
  activeView,
  userId,
  onSendMessage,
  onClose,
  areVerificationStagesHidden,
  openingId,
  isContactInfoVisible,
  modalOptions,
  shouldFetchApplicant,
}) => {
  const dispatch = useAppDispatch();

  const history = useHistory();

  const location = useLocation();

  const [activeStep, setActiveStep] = useState(activeView || InviteToInterviewViews.USER_DETAILS);

  const { data: sessionData } = useSessionQuery();

  const company = sessionData?.company;
  const companyName = company?.name;

  const [message, setMessage] = useState(getInviteMessage(companyName));

  const recruiter = useAppSelector(getRecruiter);

  const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });

  const { data: verifiedUserData, loading: isVerifiedUserLoading } = useGetVerifiedUserQuery({
    variables: {
      recruiterCurrencyCode: recruiter?.country?.currency || DEFAULT_CURRENCY_CODE,
      userId: userId,
    },
  });

  const [createVerifiedApplicant] = useCreateVerifiedUserApplicantMutation({
    onCompleted: () => {
      onMessaged?.(userId);
    },
  });

  const handleSendMessage = () => {
    if (onSendMessage) {
      onSendMessage({ message, userId });
    } else {
      createVerifiedApplicant({
        variables: {
          message,
          openingId,
          userId,
        },
      });
    }

    trackEvent({
      context: MetricContext.TALENT_AGENT_SEND_MESSAGE_MODAL,
      interaction: MetricAction.SEND_MESSAGE_TO_VERIFIED_TALENT,
      payload: {
        'User Id': userId,
      },
    });
    setActiveStep(InviteToInterviewViews.MESSAGE_SENT);
  };

  const handleMessageChange = (message: string): void => {
    setMessage(message);
  };

  const handleCloseModal = () => {
    if (queryParams?.[VIEW_VERIFIED_USER_QUERY_PARAM]) {
      const updatedQueryParams = omit(queryParams, [VIEW_VERIFIED_USER_QUERY_PARAM]);

      history.replace({
        pathname: location.pathname,
        search: qs.stringify(updatedQueryParams, { addQueryPrefix: true }),
      });
    }

    dispatch(closeModal());

    onClose?.();
  };

  if (!verifiedUserData && isVerifiedUserLoading) {
    return (
      <div className="invite-to-interview-loading">
        <CircularLoading size={Size.MEDIUM} />
      </div>
    );
  }

  return (
    <Stack
      currentStackKey={activeStep}
      className="invite-to-interview__stack"
      stack={[
        {
          className: 'invite-to-interview__user-details',
          content: (
            <TalentCard
              areVerificationStagesHidden={areVerificationStagesHidden}
              isFooterVisible={true}
              isHideActionVisible={true}
              isModalView={true}
              isVisible={true}
              onClose={handleCloseModal}
              isContactInfoVisible={isContactInfoVisible}
              openingId={openingId}
              shouldFetchApplicant={shouldFetchApplicant}
              userId={userId}
              wrapperClassName="invite-to-interview__card-wrapper"
            />
          ),
          key: InviteToInterviewViews.USER_DETAILS,
        },
        {
          content: verifiedUserData?.verifiedUser && (
            <SendMessage
              message={message}
              modalOptions={modalOptions}
              onClose={() => dispatch(closeModal())}
              onMessageChange={handleMessageChange}
              onMessageSend={handleSendMessage}
              verifiedUser={verifiedUserData?.verifiedUser}
            />
          ),
          key: InviteToInterviewViews.MESSAGE,
        },
        {
          content: <MessageSent closeModal={handleCloseModal} />,
          key: InviteToInterviewViews.MESSAGE_SENT,
        },
      ]}
    />
  );
};
